<template>
  <div class="run-schedule-calculation">
    <run-schedule-collapse mode="calculation" />
    <div class="run-schedule-calculation-control flex flex-space-between mt-60">
      <city-autocomplete
        v-model="dispatch"
        label="Откуда"
        large
        @change="onChangeDispatch"
      />
      <city-autocomplete
        v-model="destination"
        label="Куда"
        large
        @change="onChangeDestination"
      />
      <div class="run-schedule-calculation-date">
        <vz-datepicker
          v-model="currentDate"
          label="Дата доставки"
          large
          :width="330"
          :min-date="minDate"
          @change="onChangeCurrentDate"
        />
      </div>
    </div>
    <vz-warning-text v-if="hasFake" class="mt-30">
      <span v-html="fakeLocationText" />
    </vz-warning-text>
    <div v-else-if="deliveryData" class="run-schedule-calculation-delivery mt-30">
      <vz-row class="text-medium run-schedule-calculation-delivery-header">
        <vz-col /> 
        <vz-col>До адреса</vz-col>
        <vz-col>До терминала</vz-col>
      </vz-row>
      <vz-row>
        <run-schedule-calculation-item
          title="От адреса"
          :date="fromAToA?.dispatch?.date"
          :time="fromAToA?.dispatch?.time"
        />
        <run-schedule-calculation-item
          title="До адреса"
          :date="fromAToA?.destination?.date"
          :time="fromAToA?.destination?.time"
          class="run-schedule-calculation-item-mobile"
        />
        <run-schedule-calculation-item
          title="До терминала"
          :date="fromAToT?.destination?.date"
          :time="fromAToT?.destination?.time"
          :terminal="fromAToT?.destination?.text"
          :city="to"
          class="run-schedule-calculation-item-mobile"
        />
      </vz-row>
      <vz-row>
        <run-schedule-calculation-item
          title="От терминала"
          :date="fromTToA?.dispatch?.date"
          :time="fromTToA?.dispatch?.time"
          :terminal="fromTToA?.dispatch?.text"
          :city="from"
        />
        <run-schedule-calculation-item
          title="До адреса"
          :date="fromTToA?.destination?.date"
          :time="fromTToA?.destination?.time"
          class="run-schedule-calculation-item-mobile"
        />
        <run-schedule-calculation-item
          title="До терминала"
          :date="fromTToT?.destination?.date"
          :time="fromTToT?.destination?.time"
          :terminal="fromTToT?.destination?.text"
          :city="to"
          class="run-schedule-calculation-item-mobile"
        />
      </vz-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useCityStore } from '~/stores/city'
import { getScheduleDelivery } from '@/api/city'
import { Location } from '~/types/types'
import { formatDate } from '~/common/date'
import CityAutocomplete from '~/components/autocomplete/city-autocomplete.vue'
import RunScheduleCollapse from '~/components/public/run-schedule/run-schedule-collapse.vue'
import RunScheduleCalculationItem from '~/components/public/run-schedule/run-schedule-calculation-item.vue'
import VzWarningText from '~/components/ui-kit/vz-warning-text.vue';
import {fakeLocationText} from '~/common/objects';
import VzDatepicker from "~/components/ui-kit/vz-datepicker/vz-datepicker.vue";

const props = defineProps({
  date: {
    type: String,
    default: ''
  }
})

const emit = defineEmits([
  'change-date'
])

const dispatch = ref('')
const destination = ref('')
const currentDate = ref('')
const deliveryData = ref([] as any[])

const { from, to } = storeToRefs(useCityStore())
const { setFrom, setTo } = useCityStore()

const minDate = computed((): string  => {
  return formatDate(new Date(), 'YYYY-MM-DD')
})

const fromAToA = computed(() => {
  return deliveryData.value.find(v => v.type?.dispatch === 'address' && v.type?.destination === 'address')
})

const fromAToT = computed(() => {
  return deliveryData.value.find(v => v.type?.dispatch === 'address' && v.type?.destination === 'terminal')
})

const fromTToA = computed(() => {
  return deliveryData.value.find(v => v.type?.dispatch === 'terminal' && v.type?.destination === 'address')
})

const fromTToT = computed(() => {
  return deliveryData.value.find(v => v.type?.dispatch === 'terminal' && v.type?.destination === 'terminal')
})

const hasFake = computed(() => {
  return Number(from.value?.fake) === 1 || Number(to.value?.fake) === 1
})

onMounted((): void => {
  nextTick(() => {
    currentDate.value = props.date
    dispatch.value = from.value.guid
    destination.value = to.value.guid
    getDeliveryData(dispatch.value, destination.value, currentDate.value)
  })
})

function onChangeDispatch(value: string, location: Location): void {
  setFrom(location)
  getDeliveryData(value, destination.value, currentDate.value)
}

function onChangeDestination(value: string, location: Location): void {
  setTo(location)
  getDeliveryData(dispatch.value, value, currentDate.value)
}

function onChangeCurrentDate(value: string): void {
  emit('change-date', value)
  getDeliveryData(dispatch.value, destination.value, value)
}

async function getDeliveryData(dispatchValue: string, destinationValue: string, dateValue: string): Promise<void> {
  deliveryData.value = await getScheduleDelivery({ dispatch: dispatchValue, destination: destinationValue, date: dateValue })
}
</script>